import { APP_ROUTES, DEFAULT_ERROR_MESSAGE, FlyerType, SubscriptionTypeEnum } from '@/config/constants';
import AuthenticationContext from '@/context/AuthenticationContext';
import ScrollRestorationContext from '@/context/ScrollRestorationContext';
import {
  BodyMediumEmphasized,
  ExpiredTag,
  Tag,
  useSnackbar
} from '@/design-system';
import { SAVE_FLYER_MUTATION } from '@/graphql/mutation';
import { districtVar, openLoginDrawerVar } from '@/graphql/state';
import { SaveFlyerResponse } from '@/graphql/types';
import { useMetrics } from '@/hooks';
import { useAnalytics } from '@/hooks/useAnalytics';
import { FlyerStatus } from '@/types/flyer';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { useMutation, useReactiveVar } from '@apollo/client';
import { cx } from '@emotion/css';
import { Skeleton, Stack } from '@mui/material';
import { useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AccessibleDivButton } from '../AccessibleDivButton';
import { FavoriteIcon } from '../FavoriteIcon';
import styles from './FlyerCard.style';

interface FlyerCardProps {
  id: number;
  thumbnail: string;
  title: string;
  isFavorite: boolean;
  onFavoriteFlyer: (flyerId: number) => void;
  trackId?: string;
  inactive?: boolean;
  status?: FlyerStatus;
  criticalDate?: string;
  isRead?: boolean;
  audienceId?: string;
  districtId?: string | null;
  flyerType?: string;
  userText?: string;
  email?: string;
}

export const FlyerCard: React.FC<FlyerCardProps> = ({
  id,
  thumbnail,
  title,
  isFavorite,
  onFavoriteFlyer,
  trackId,
  inactive = false,
  status,
  criticalDate,
  isRead = false,
  audienceId,
  districtId,
  flyerType,
  userText,
  email,
}) => {
  const { saveScrollY } = useContext(ScrollRestorationContext);
  const district = useReactiveVar(districtVar);
  const { track } = useAnalytics();
  const showToast = useSnackbar();
  const navigate = useNavigate();
  const [saveFlyer,] = useMutation<SaveFlyerResponse>(SAVE_FLYER_MUTATION);
  const [loadingFlyer, setLoadingFlyer] = useState(true);
  const { trackMetrics } = useMetrics();
  const { search } = useLocation();
  const audienceType = new URLSearchParams(search).get('audienceType')
  const onLoadFlyer = () => {
    setLoadingFlyer(false);
  };

  const isSchool = flyerType === FlyerType.school && !!audienceId && audienceId !== 'all';
  const resourceId = isSchool ? audienceId : district?.districtId.toString() ?? '';
  const resource = isSchool ? "schools" : "districts"

  const { isAuthenticated } = useContext(
    AuthenticationContext,
  );


  // const renderTag = () => {
  //   let component;
  //   if (status === FlyerStatus.expired) {
  //     component = <ExpiredTag />;
  //   } else if (criticalDate) {
  //     const criticalTagPos = Math.round(
  //       Math.random() * (criticalTags.length - 1) + 1,
  //     );
  //     const criticalTag = criticalTags[criticalTagPos - 1];
  //     const variant: TagVariantType =
  //       criticalTag === 'Due this week' ? 'deadline' : 'timeline';
  //     component = <Tag variant={variant}>{criticalTag}</Tag>;
  //   }

  //   if (component) {
  //     return <div className={styles.tagContainer}>{component}</div>;
  //   }
  //   return null;
  // };

  const onClickFavoriteFlyer = async () => {
    if (!isAuthenticated) {
      openLoginDrawerVar(true);
      return;
    }
    try {
      const { data } = await saveFlyer({
        variables: {
          input: {
            flyerId: id,
            isFavorite: !isFavorite
          },
        },
      });
      if (data?.saveFlyer.flyerId) {
        onFavoriteFlyer(id);
        const action = isFavorite ? 'pa-unsave' : 'pa-save';

        trackMetrics({
          flyerId: id.toString(),
          action,
          metric: 'thumbnail-save',
          resource,
          resourceId,
          audienceId,
        });
        if (!isFavorite) {
          showToast({
            message: 'The flyer has been saved.',
            buttonLabel: 'View Saved',
            onClick: () => navigate(APP_ROUTES.saved),
            duration: 5000
          })
        } else {
          showToast({
            message: 'Flyer unsaved.',
            duration: 5000
          })
        }

      }
    } catch (err) {
      showToast({ message: DEFAULT_ERROR_MESSAGE });
    }
  }

  const onFlyerClick = () => {
    track({
      action: GenericTrackTypes.LINK_PRESSED,
      category: `${trackId}:go_to_flyer_details`,
      label: title,
      value: Number(id),
    });


    // Temporarily(?) disabling this metrics event
    // trackMetrics({
    //   flyerId: id.toString(),
    //   metric: 'viewflyer',
    //   action: 'thumbnail-view',
    //   resource,
    //   resourceId,
    // });

    saveScrollY(window.pageYOffset);
  }

  const FlyerCardLink = ({ children }: { children: React.ReactNode }) => (
    <Link
      to={`/district/${districtId || district?.districtId}/flyer/${id}${(audienceId && audienceType !== SubscriptionTypeEnum.district) ? `?audienceId=${audienceId}` : ''}`}
      state="flyerboard"
      onClick={onFlyerClick}
      className={styles.link}
    >
      {children}
    </Link>
  )

  return (
    <>
      {loadingFlyer && (
        <div className={styles.loader}>
          <Stack spacing={1}>
            <Skeleton variant="rectangular" width="100%" height={150} />
            <Skeleton variant="text" />
          </Stack>
        </div>
      )}
      <div>
        <div
          className={cx(styles.flyerCard, inactive ? styles.inactiveFlyer : {})}
          key={id}
          style={loadingFlyer ? { display: 'none' } : {}}
          data-testid="flyerCard"
        >
          {isRead && (
            <div className={styles.viewedTag}>
              <Tag variant="viewed">Viewed</Tag>
            </div>
          )}
          <FlyerCardLink>
            <img
              src={thumbnail}
              alt={`Image of a flyer titled ${title}`}
              aria-describedby={`flyer-Description-${id}`}
              onLoad={onLoadFlyer}
              data-testid="flyerCardThumbnail"
            />
            <span
              id={`flyer-Description-${id}`}
              className={styles.flyerDescription}
              data-testid="flyer-description"
            >
              Text Found On Flyer -
              {userText || 'No Text Available'} Need more information on this
              flyer? You can email the person who submitted the flyer at{' '}
              {email} to learn more.
            </span>
          </FlyerCardLink>
          <div className={styles.flyerContent}>
            <FlyerCardLink>
              <BodyMediumEmphasized
                data-testid="flyerCardTitle"
                className={styles.flyerTitle}
                nonTranslate
              >
                {title}
              </BodyMediumEmphasized>
            </FlyerCardLink>
            <AccessibleDivButton
              onClick={onClickFavoriteFlyer}
              data-testid="flyerCardFavoriteButton"
            >
              <FavoriteIcon isFavorite={isFavorite} size={22} />
            </AccessibleDivButton>
          </div>
        </div>
        {status === FlyerStatus.expired && (
          <div className={styles.tagContainer}>
            <ExpiredTag />
          </div>
        )}

        {/* {status === FlyerStatus.expired ? (
          <div className={styles.tagContainer}>
            <ExpiredTag />
          </div>
        ) : (
          <div className={styles.tagContainer}>{criticalDate}</div>
        )} */}
        {/* {renderTag()} */}
      </div>
    </>
  );
};
